import { models } from 'powerbi-client';
import { useMemo } from 'react';
import { useGlobalFilters } from '../../context/GlobalFilterProvider';
import { useNavigationContext } from '../../context/NavigationProvider';
import {
  disabledFilters,
  GlobalFilterType,
} from '../../features/GlobalFilters';
import { useCurrentUser } from '../../hooks/queries';
import { generateFilters } from '../../lib/utils';
import { currencyPowerBiMapping } from '../../utils/principal-service/user-endpoints.types';
import { SecondaryFeature } from '../../utils/types';

const PRODUCT_TABLE_FEATURES = new Set([
  SecondaryFeature.REPORTING_OPPORTUNITIES,
  SecondaryFeature.REPORTING_LEADS,
  SecondaryFeature.REPORTING_INCIDENTS,
  SecondaryFeature.REPORTING_PRODUCT_SAMPLES,
  SecondaryFeature.LABS_AND_FORMULATIONS_FORMULATIONS,
]);

export function usePowerBiFilters() {
  const { globalFilters } = useGlobalFilters();
  const { tertiaryFeature, secondaryFeature, primaryFeature } =
    useNavigationContext();
  const { user } = useCurrentUser();

  const currentFeature =
    tertiaryFeature?.id ?? secondaryFeature?.id ?? primaryFeature?.id;

  const powerBiFilters = useMemo(() => {
    if (
      !currentFeature ||
      !globalFilters.countries ||
      !globalFilters.portfolios ||
      !globalFilters.marketSegments
    ) {
      return null;
    }
    const countryCodes =
      globalFilters.countries?.map((country) => country.iso) ?? [];

    const portfolioTableName =
      secondaryFeature && PRODUCT_TABLE_FEATURES.has(secondaryFeature.id)
        ? 'Products'
        : 'Items';

    return [
      !disabledFilters
        ?.get(currentFeature)
        ?.includes(GlobalFilterType.PORTFOLIOS) &&
        generateFilters(
          portfolioTableName,
          'Portfolio Key',
          globalFilters?.portfolios?.map((portfolio) => portfolio.id) || [],
        ),
      !disabledFilters
        ?.get(currentFeature)
        ?.includes(GlobalFilterType.COUNTRIES) &&
        generateFilters('Company', 'Country Code', countryCodes),
      !disabledFilters
        ?.get(currentFeature)
        ?.includes(GlobalFilterType.MARKET_SEGMENTS) &&
        generateFilters(
          'Market Segment',
          'Market Segment Code',
          globalFilters?.marketSegments
            ?.map((marketSegment) => marketSegment.code)
            .filter((m): m is string => typeof m === 'string') || [],
        ),
      globalFilters.territories &&
        !disabledFilters
          ?.get(currentFeature)
          ?.includes(GlobalFilterType.TERRITORIES) &&
        generateFilters('Territory', 'Territory', [
          ...(globalFilters?.territories?.map((territory) => territory.name) ??
            []),
          null, // Non-USA countries have null for their territory, so this is required so they aren't removed
        ]),
      generateFilters('Currency', 'Currency', [
        currencyPowerBiMapping[user.currency],
      ]),
    ].filter(
      (f) => f && (f.operator === 'All' || f.values.length > 0),
    ) as models.IBasicFilter[];
  }, [
    currentFeature,
    globalFilters.countries,
    globalFilters?.marketSegments,
    globalFilters?.portfolios,
    globalFilters?.territories,
    secondaryFeature,
    user.currency,
  ]);

  return { powerBiFilters };
}
